// src/components/SearchResults.jsx — /search?q= results page
import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getProducts } from "../api";
import { useWishlist } from "../context/WishlistContext";
import QuickAddModal from "./QuickAddModal";

const BRAND = "var(--brand)";
const taka = (n) => `\u09F3${Number(n || 0).toLocaleString("en-BD")}`;
const imgFallback = (e, label = "RAINZ") => { e.target.onerror = null; e.target.src = `https://placehold.co/600x800/f3f4f6/9ca3af?text=${encodeURIComponent(label)}`; };

function ProductTile({ product, onOpen, onAdd, onBuyNow }) {
  const { has, toggle } = useWishlist();
  const wished = has(product.id);
  const discount = product.oldPrice && product.oldPrice > product.price ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) : 0;
  return (
    <div className="group relative rounded-xl border shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden flex flex-col" style={{ backgroundColor: "var(--foreground)", borderColor: "var(--border)" }}>
      {discount > 0 && <span className="absolute top-2 left-2 z-10 text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: BRAND, color: "var(--button-text)" }}>-{discount}%</span>}
      <button aria-label="Wishlist" onClick={(e) => { e.stopPropagation(); toggle(product); }} className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full border backdrop-blur-sm shadow flex items-center justify-center" style={{ backgroundColor: "rgba(11,11,11,.6)", borderColor: "var(--border)", color: wished ? BRAND : "var(--subtitle)" }}>
        {wished ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
      </button>
      <div className="h-52 sm:h-60 flex items-center justify-center p-3 cursor-pointer" style={{ background: "linear-gradient(to bottom, #1F1F1F, #0F0F0F)" }} onClick={() => onOpen(product)}>
        <img src={product.image} alt={product.name} loading="lazy" className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105" onError={(e) => imgFallback(e, product.name)} />
      </div>
      <div className="px-3 pt-2 pb-3 flex flex-col">
        <p className="text-sm truncate cursor-pointer hover:underline" style={{ color: "var(--details)" }} onClick={() => onOpen(product)}>{product.name}</p>
        <div className="flex items-center gap-2 mt-0.5 h-5">
          <span className="text-sm font-bold" style={{ color: "var(--title)" }}>{taka(product.price)}</span>
          {product.oldPrice && <span className="text-xs line-through" style={{ color: "var(--subtitle)", opacity: 0.7 }}>{taka(product.oldPrice)}</span>}
        </div>
        <div className="mt-2 grid grid-cols-1 gap-1.5">
          <button onClick={(e) => { e.stopPropagation(); onAdd(product); }} className="w-full flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-semibold transition-opacity hover:opacity-90" style={{ backgroundColor: BRAND, color: "var(--button-text)" }}>
            <ShoppingBagOutlinedIcon style={{ fontSize: 15 }} /> Add to Bag
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") || "";

  const [input, setInput] = useState(q);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [quickSlug, setQuickSlug] = useState(null);

  const runSearch = useCallback((term) => {
    if (!term.trim()) { setItems([]); setTotal(0); return; }
    setLoading(true);
    getProducts({ search: term.trim(), pageSize: 48 })
      .then((res) => { setItems(res.items || []); setTotal(res.total ?? (res.items || []).length); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, []);

  // Re-run whenever the URL ?q= changes
  useEffect(() => { setInput(q); runSearch(q); }, [q, runSearch]);

  const submit = (e) => {
    e.preventDefault();
    const term = input.trim();
    if (term) setParams({ q: term });   // updates URL → triggers effect
  };

  return (
    <div className="w-full max-w-[1500px] mx-auto px-4 py-5 sm:py-8 pb-24 lg:pb-8 overflow-x-hidden">
      <nav className="text-xs mb-3 flex items-center flex-wrap gap-y-1" style={{ color: "var(--title)" }}>
        <Link to="/" className="no-underline px-1.5 py-0.5 rounded" style={{ color: "var(--title)" }}>Home</Link>
        <ChevronRightIcon style={{ fontSize: 14, color: "var(--subtitle)" }} className="mx-0.5" />
        <span className="px-1.5 py-0.5" style={{ color: "var(--subtitle)" }}>Search</span>
      </nav>

      {/* Search box */}
      <form onSubmit={submit} className="flex items-center rounded-full px-4 py-2.5 mb-5 max-w-2xl border" style={{ backgroundColor: "var(--foreground)", borderColor: "var(--border)" }}>
        <input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search for products, brands and more"
          className="flex-1 min-w-0 bg-transparent text-sm outline-none" style={{ color: "var(--details)" }}
        />
        <button type="submit" aria-label="Search" style={{ color: "var(--subtitle)" }}><SearchIcon fontSize="small" /></button>
      </form>

      {/* Heading */}
      {q && (
        <h1 className="text-lg sm:text-xl font-bold mb-4" style={{ color: "var(--title)" }}>
          {loading ? "Searching…" : `${total} result${total !== 1 ? "s" : ""} for "${q}"`}
        </h1>
      )}

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-72 rounded-xl border animate-pulse" style={{ backgroundColor: "rgba(212,175,55,.06)", borderColor: "var(--border)" }} />)}
        </div>
      ) : !q ? (
        <div className="text-center py-20" style={{ color: "var(--subtitle)" }}>
          <SearchIcon style={{ fontSize: 48 }} />
          <p className="mt-2 text-sm" style={{ color: "var(--subtitle)" }}>Type something above to search products.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20">
          <p className="font-medium" style={{ color: "var(--details)" }}>No products found for "{q}".</p>
          <p className="text-sm mt-1" style={{ color: "var(--subtitle)" }}>Try a different keyword or check the spelling.</p>
          <Link to="/new-arrivals" className="inline-block mt-4 rounded-full px-6 py-2 text-sm font-semibold" style={{ backgroundColor: BRAND, color: "var(--button-text)" }}>Browse New Arrivals</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {items.map((p) => (
            <ProductTile
              key={p.id ?? p.slug}
              product={p}
              onOpen={(prod) => navigate(`/product/${prod.slug}`)}
              onAdd={(prod) => setQuickSlug(prod.slug)}
              onBuyNow={(prod) => navigate(`/product/${prod.slug}`)}
            />
          ))}
        </div>
      )}

      <QuickAddModal slug={quickSlug} onClose={() => setQuickSlug(null)} />
    </div>
  );
}